import { Person } from "@mui/icons-material";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  getSelectedMatch,
  unsetSelectedMatch,
} from "app/reducers/matchesSlice";
import { MODAL_TYPES, openModal } from "app/reducers/modalSlice";
import {
  getSessionActionState,
  unsetSession,
} from "app/reducers/sessionsSlice";
import { getSlotState } from "app/reducers/slotsSlice";
import { getSoundsActionState } from "app/reducers/soundsSlice";
import { getMatchStateDot } from "features/container-card/ContainerCard";
import { LogsContainer } from "features/container-card/LogsContainer";
import { MATCH_STATUS } from "interfaces/Match";
import React, { useCallback, useEffect, useRef } from "react";
import { HeadsetStatus, SessionIcon, Spinner, useCustomToast } from "ui";

export const DevicesSessionSideBar = () => {
  const dispatch = useAppDispatch();
  const { isError, isSuccess, error } = useAppSelector(getSoundsActionState);
  const { session } = useAppSelector(getSessionActionState);
  const { selectedMatch } = useAppSelector(getSelectedMatch);
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const { slots } = useAppSelector(getSlotState);

  const selectedMatchRef = useRef(selectedMatch);

  useEffect(() => {
    dispatch(unsetSelectedMatch());
    dispatch(unsetSession());
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      showErrorToast({
        title: "Error",
        message: error?.message || "Algo salio mal.",
      });
    }
  }, [dispatch, error, isError, showErrorToast]);

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast({
        title: "Éxito",
        message: "Sonido Emitido",
      });
    }
  }, [dispatch, isSuccess, showSuccessToast]);

  const renderMatchInfo = useCallback(() => {
    if (!selectedMatch || !session) return;

    const {
      _id: matchId,
      headset,
      logs,
      container,
      playerAlias,
      status,
    } = selectedMatch;

    if (!logs || !container || !headset) return;

    const headsetNumber = slots.find(
      (slot) => slot.deviceId === selectedMatch.headsetId,
    )?.number;

    return (
      <div className="flex flex-col overflow-hidden max-h-screen">
        <div className="flex flex-col gap-4 h-1/2 ml-6 my-8 justify-around bg-dark-gray-blue">
          <div className="flex items-center gap-4">
            <SessionIcon />
            <h3 className="text-2xl">{session.config.sessionNumber}</h3>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-lg">Contenedor {container[0].number}</p>
            <HeadsetStatus
              isConnected={!!headset[0]?.connectionId}
              batteryValue={headset[0].battery}
            />
          </div>
          <div>
            <p className="text-faux-shadow-blue text-sm">Jugador</p>
            <p> {playerAlias}</p>
          </div>
          <div>
            <p className="text-faux-shadow-blue text-sm">Casco</p>
            <p>{`Casco ${headsetNumber}`}</p>
          </div>
          <div className="flex flex-col">
            <p className="text-faux-shadow-blue text-sm">Estado de partida</p>
            <p>
              {getMatchStateDot(status)}
              {MATCH_STATUS[status]}
            </p>
          </div>
        </div>
        <div className="flex flex-col pt-6 pl-6 pb-8 bg-faux-gunmetal overflow-y-auto h-screen">
          <p className="text-xl mb-2"> Juego</p>
          <LogsContainer data={logs} />
        </div>
        <div className="p-6 bg-faux-gunmetal text-center">
          {status === "IN_PROGRESS" && session.matches.length > 1 && (
            <>
              <MUIButton
                className="text-lg px-6 py-2"
                variant="outlined"
                onClick={() => {
                  dispatch(
                    openModal({ type: MODAL_TYPES.CONFIRMATION, matchId }),
                  );
                }}
                startIcon={<Person />}
              >
                Finalizar partida
              </MUIButton>
            </>
          )}
        </div>
      </div>
    );
  }, [dispatch, selectedMatch, session, slots]);

  if (!session && !selectedMatchRef) {
    return (
      <div className="flex w-full justify-center items-center bg-dark-gray-blue h-screen">
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <div className="bg-dark-gray-blue flex flex-col h-full">
      {!selectedMatch ? (
        <div className="flex flex-col gap-6 ml-8 h-full justify-center">
          <div className="flex items-center gap-4">
            <SessionIcon />
            <h3 className="text-2xl">{session?.config.sessionNumber}</h3>
          </div>
          <p className="text-xl">
            Ingresa a cualquier contenedor para ver la partida y gestionar
            ayuda.
          </p>
        </div>
      ) : (
        renderMatchInfo()
      )}
    </div>
  );
};
